import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table
} from 'reactstrap';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaCalendarAlt, FaDownload, FaUser, FaFileAlt, FaPrint, FaSearch } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useReactToPrint } from 'react-to-print';
import { clientActions } from '../../sagas/clientSlice';
import Select from "react-select";
import autoTable from 'jspdf-autotable';
import { transporteurActions } from '../../sagas/transporteurSlice';

const VentesStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);

  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const [filters, setFilters] = useState({
    clientId: null,
    transporteurId: null,
    chantierId: null,
    dateDebut: formatDate(todayDate),
    dateFin: formatDate(tomorrowDate),
    query: ''
  });

  useEffect(() => {
    dispatch(saleActions.clearClientReport());
    generateClientReport()
  }, [filters.clientId, filters.transporteurId, filters.chantierId, filters.dateDebut, filters.dateFin]);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();

    if (dateString.includes('-')) {
      return parseISO(dateString);
    }

    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  // Client report state
  const { clients, loading } = useSelector(state => state.clients);
  const { clientReport, clientReportLoading, clientReportError } = useSelector(state => state.sales);
  const [selectedClient, setSelectedClient] = useState('');
  const clientReportRef = useRef(null);
  const { transporteurs } = useSelector(state => state.transporteurs);

  const clientOptions = clients?.map(client => ({
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.nom} ${client.prenom || ''} `
      : client.entreprise,
    chantiers: client.chantiers || []
  })) ?? [];

  const clientOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...clientOptions
  ];

  const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
    value: chantier.id,
    label: `${chantier.code} - ${chantier.nom}`,
    ...chantier
  })) || [];

  const chantierOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...chantierOptions
  ];

  // Effect for fetching data
  useEffect(() => {
    if (clients.length === 0) {
      // Fetch clients for client report
      const fetchClients = async () => {
        dispatch(clientActions.search({
          query: ""
        }));
      };

      fetchClients();
    }
    dispatch(transporteurActions.list());

  }, []);

  const debouncedSearch = () => {
    dispatch(saleActions.generateClientReport({ ...filters }));
  }


  // Generate client report
  const generateClientReport = () => {
    dispatch(saleActions.generateClientReport({ ...filters }));
  };


  const exportClientReportToPDF = () => {
    if (!clientReport) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const footerHeight = 15;

    // ---- GREEN & WHITE THEME (SAME AS SUPPLIER) ----
    const primaryGreen = { r: 46, g: 204, b: 113 };
    const altRow = { r: 232, g: 248, b: 245 };
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };
    const negativeRed = { r: 231, g: 76, b: 60 };

    // ---------------- HEADER ----------------
    const addPageHeader = () => {
      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.rect(0, 25, pageWidth, 5, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("RAPPORT VENTE CLIENT", pageWidth / 2, 15, { align: "center" });

      const from = format(parseDate(clientReport.period.from), "dd/MM/yyyy", { locale: fr });
      const to = format(parseDate(clientReport.period.to), "dd/MM/yyyy", { locale: fr });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Période: du ${from} au ${to}`, pageWidth / 2, 22, { align: "center" });
    };

    // ---------------- FOOTER ----------------
    const addPageFooter = () => {
      const y = pageHeight - footerHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document généré le: ${clientReport.generationDate}`, pageWidth - margin, y + 5, { align: "right" });
      doc.text(company.name, margin, y + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, y + 5, { align: "center" });
    };

    // ---- FIRST PAGE ----
    addPageHeader();

    // ---- COMPANY INFO ----
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(black.r, black.g, black.b);
    doc.text(company.name, margin, 40);

    doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Adresse: ${company.address}`, margin, 47);
    doc.text(`Tél: ${company.phone1} | Email: ${company.email}`, margin, 52);
    doc.text(`RC: ${company.rc} | IF: ${company.identificationFiscale} | ICE: ${company.ice}`, margin, 57);

    // ---- CLIENT BOX ----
    if (clientReport.client) {
      doc.setFillColor(white.r, white.g, white.b);
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("CLIENT", pageWidth - margin - 75, 42);

      doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(black.r, black.g, black.b);
      doc.text(`Nom: ${clientReport.client.name}`, pageWidth - margin - 75, 50);
      doc.text(`Téléphone: ${clientReport.client.phone}`, pageWidth - margin - 75, 55);
      doc.text(`Code: ${clientReport.client.code}`, pageWidth - margin - 75, 60);
    }

    // ---------------- TABLE ----------------
    const headers = ["N° BL", "Date Livraison", "Désignation", "Qté", "UM", "PU", "Total"];

    const rows = clientReport.deliveryNotes.map(bl => [
      bl.blNumber,
      format(parseDate(bl.date), "dd/MM/yyyy", { locale: fr }),
      bl.designation,
      bl.quantity.toString(),
      bl.unit,
      formatPrice(bl.unitPrice, false),
      formatPrice(bl.lineTotal, false)
    ]);

    autoTable(doc, {
      startY: 70,
      head: [headers],
      body: rows,
      theme: "grid",

      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        lineColor: [0, 0, 0]
      },

      alternateRowStyles: {
        fillColor: [altRow.r, altRow.g, altRow.b]
      },

      styles: {
        cellPadding: 3,
        fontSize: 9,
        textColor: [0, 0, 0],
        lineWidth: 0.25,
        lineColor: [150, 150, 150]
      },

      margin: { top: margin, bottom: footerHeight },
      didDrawPage: addPageFooter
    });

    if (clientReport.summary) {
      const s = clientReport.summary;

      let summaryY = doc.lastAutoTable.finalY + 15;
      const SUMMARY_HEIGHT = 65;

      // 🔴 PAS ASSEZ DE PLACE → NOUVELLE PAGE
      if (summaryY + SUMMARY_HEIGHT > pageHeight - footerHeight) {
        doc.addPage();
        addPageHeader();
        summaryY = 40;
      }

      // ---------------- SUMMARY BOX ----------------
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, summaryY, 90, 40, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("RÉSUMÉ", margin + 5, summaryY + 7);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);

      doc.text("Montant Total Consommé:", margin + 5, summaryY + 15);
      doc.text(formatPrice(s.totalConsome, false), margin + 85, summaryY + 15, { align: "right" });

      doc.text("Solde Avant:", margin + 5, summaryY + 22);
      doc.text(formatPrice(s.soldeAvant, false), margin + 85, summaryY + 22, { align: "right" });

      doc.text("Avance:", margin + 5, summaryY + 29);
      doc.text(formatPrice(s.avance, false), margin + 85, summaryY + 29, { align: "right" });

      doc.text("Avoir:", margin + 5, summaryY + 36);
      doc.text(formatPrice(s.avoir, false), margin + 85, summaryY + 36, { align: "right" });

      // ---------------- NET À PAYER ----------------
      const net = parseFloat(s.netApaye);
      const netY = summaryY + 55;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(net < 0 ? negativeRed.r : primaryGreen.r,
        net < 0 ? negativeRed.g : primaryGreen.g,
        net < 0 ? negativeRed.b : primaryGreen.b);

      doc.text("Net À Payer :", margin, netY);
      doc.text(formatPrice(net, false), margin + 60, netY);
    }


    // ---------------- OPEN IN NEW TAB ----------------
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };


  const transporteurOptions = transporteurs?.map(transporteur => ({
    value: transporteur.id,
    label: transporteur.code + ' - ' + transporteur.libelle,
  })) ?? [];


  return (
    <Container fluid className="page-content">
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFileAlt className="me-2 text-primary" />
              Détails des ventes
            </h5>

            <div className="d-flex gap-2">
              <Button
                color="success"
                size="sm"
                onClick={generateClientReport}
                disabled={clientReportLoading}
              >
                {clientReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Génération...
                  </>
                ) : (
                  <>
                    <FaFileAlt className="me-1" />
                    Générer
                  </>
                )}
              </Button>

              {clientReport && (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={exportClientReportToPDF}
                >
                  <FaDownload className="me-1" />
                  Export PDF
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-4">
            <Col lg={3} md={3} sm={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1">{t('reglement.search')}</Label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    placeholder={t('bonLivraison.searchPlaceholder')}
                    value={filters.query}
                    onChange={e => {
                      const value = e.target.value.toUpperCase();
                      setFilters(prevFilters => ({
                        ...prevFilters,
                        query: value
                      }));
                    }}
                    className="text-uppercase"
                    style={{ textTransform: 'uppercase' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        debouncedSearch(e.target.value.toUpperCase());
                      }
                    }}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="clientSelect" className="form-label">
                  <FaUser className="me-1" /> Client
                </Label>
                <Select
                  id="clientId"
                  options={clientOptionsWithAll}
                  value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                  onChange={(selectedOption) => {
                    setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                    setSelectedClient(selectedOption);
                  }}
                  placeholder={t('bonLivraison.selectClient')}
                  isSearchable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => setFilters(f => ({ ...f, dateDebut: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => setFilters(f => ({ ...f, dateFin: e.target.value }))}
                />
              </FormGroup>
            </Col>

          </Row>

          {/* Client Report Content */}
          <div ref={clientReportRef}>
            {clientReport ? (
              <div className="client-report p-4 border rounded-4 bg-white shadow-sm animate__animated animate__fadeIn">
                {/* <div className="report-header mb-4 pb-2 border-bottom">
                  <div className="text-center mb-3">
                    <h4 className="text-primary mb-1 fw-bold letter-spacing-1">RAPPORT VENTE CLIENT</h4>
                    <p className="text-muted small mb-0">
                      <FaCalendarAlt className="me-1" />
                      Période: <span className="fw-semibold">du {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                    </p>
                  </div>

                  <Row className="mb-3 g-4">
                    <Col md={6}>
                      <div className="company-info bg-light p-3 rounded-3 h-100">
                        <h5 className="mb-2 text-dark fw-bold"><FaFileAlt className="me-2 text-primary" />{company.name}</h5>
                        <div className="small text-secondary">
                          <div><span className="fw-semibold">Adresse:</span> {company.address}</div>
                          <div><span className="fw-semibold">Tél:</span> {company.phone1} <span className="mx-1">|</span> <span className="fw-semibold">Email:</span> {company.email}</div>
                          <div><span className="fw-semibold">RC:</span> {company.rc} <span className="mx-1">|</span> <span className="fw-semibold">IF:</span> {company.identificationFiscale} <span className="mx-1">|</span> <span className="fw-semibold">ICE:</span> {company.ice}</div>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="client-info bg-white border border-2 border-primary p-3 rounded-3 h-100">
                        <h6 className="mb-2 text-primary fw-bold"><FaUser className="me-2" />Informations Client</h6>
                        <div className="small">
                          <div className="mb-1"><strong>Client:</strong> {clientReport.client.name}</div>
                          <div className="mb-1"><strong>Téléphone:</strong> {clientReport.client.phone}</div>
                          <div className="mb-0"><strong>Code client:</strong> {clientReport.client.code}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div> */}

                <div className="report-body mb-4">
                  <div className="table-responsive rounded-3 overflow-hidden">

                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">N° BL</th>
                          <th className="text-center">Client</th>
                          <th className="text-center">Mode P.</th>
                          <th className="text-center">Date Livraison</th>
                          <th className="text-center">Désignation</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-center">UM</th>
                          <th className="text-end">PU</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientReport.deliveryNotes.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td className="text-center fw-semibold">{note.blNumber}</td>
                            <td className="text-center">{note.client ? `${note.client.name}(${note.client.code})` : "Passager"}</td>
                            <td className="ps-2">{note.modeReglement}</td>
                            <td className="text-center">{format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr })}</td>
                            <td className="ps-2">{note.designation}</td>
                            <td className="text-center">{note.quantity}</td>
                            <td className="text-center">{note.unit}</td>
                            <td className="text-end">{formatPrice(note.unitPrice, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="5" className="text-end fw-bold">Total:</td>
                          <td className="text-center fw-bold text-primary">
                            {clientReport.deliveryNotes.reduce((acc, note) => acc + (Number(note.quantity) || 0), 0)}
                          </td>
                          <td></td>
                          <td></td>
                          <td className="text-end fw-bold text-primary">{formatPrice(clientReport.deliveryNotes.reduce((acc, note) => acc + (note.lineTotal || 0), 0), false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                </div>


                {clientReport && clientReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(clientReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(clientReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Règlements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(clientReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                clientReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          clientReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>}

                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{clientReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {clientReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un client et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default VentesStatistiques;
